/**
 * Visual indicator shown only when the deployment is wired up to the
 * GoCardless sandbox environment. Helps donors and admins immediately
 * recognise they're not on the live platform — no real money moves,
 * no real Gift Aid claims will be filed.
 *
 * Reads the public env var (baked into the client bundle at build time)
 * and renders nothing when on live. The "show nothing on unset" default
 * is intentional — fail-safe means a misconfigured deployment never
 * mislabels itself as live.
 */

import { AlertTriangle } from 'lucide-react';

interface SandboxBadgeProps {
  variant?: 'banner' | 'inline';
}

export function SandboxBadge({ variant = 'banner' }: SandboxBadgeProps) {
  if (process.env.NEXT_PUBLIC_GOCARDLESS_ENVIRONMENT !== 'sandbox') {
    return null;
  }

  if (variant === 'inline') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-900">
        <AlertTriangle className="h-3 w-3" />
        Sandbox
      </span>
    );
  }

  return (
    <div className="mb-4 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
      <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
      <div>
        <div className="font-semibold">Sandbox mode</div>
        <div className="text-xs text-amber-800">
          This is a test environment. No real money will be charged and no Gift Aid claim will be filed.
        </div>
      </div>
    </div>
  );
}
